import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger);

// Floating SVG petals
const PETALS = [
  { top: "15%", left: "8%", delay: 0, size: 24, rotate: 20 },
  { top: "30%", left: "90%", delay: 0.5, size: 18, rotate: -30 },
  { top: "55%", left: "5%", delay: 1, size: 22, rotate: 45 },
  { top: "70%", left: "85%", delay: 0.3, size: 16, rotate: -15 },
  { top: "20%", left: "55%", delay: 0.8, size: 14, rotate: 60 },
];

function PetalSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2C12 2 8 6 8 10C8 14 12 16 12 16C12 16 16 14 16 10C16 6 12 2 12 2Z" fill="#e8b4b8" opacity="0.7" />
      <path d="M12 16C12 16 6 14 4 10C2 6 6 4 8 6" fill="#f4d4d6" opacity="0.5" />
    </svg>
  );
}

export function ParallaxLayout({ formSlot, program, title, dateTime, location, description, slug }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.5, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // Multi-layer parallax on hero
      gsap.to(".par-layer-back", {
        yPercent: -30,
        ease: "none",
        scrollTrigger: { trigger: ".par-hero", start: "top top", end: "bottom top", scrub: true },
      });
      gsap.to(".par-layer-mid", {
        yPercent: -15,
        ease: "none",
        scrollTrigger: { trigger: ".par-hero", start: "top top", end: "bottom top", scrub: true },
      });
      gsap.to(".par-layer-front", {
        yPercent: -5,
        ease: "none",
        scrollTrigger: { trigger: ".par-hero", start: "top top", end: "bottom top", scrub: true },
      });

      // Story circle expands on scroll
      gsap.from(".par-story-circle", {
        scale: 0.3,
        opacity: 0,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: { trigger: ".par-story-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });

      // Horizontal gallery — smooth scroll
      const gallery = document.querySelector<HTMLElement>(".par-gallery-track");
      if (gallery) {
        const totalWidth = gallery.scrollWidth - gallery.parentElement!.offsetWidth;
        gsap.to(gallery, {
          x: -totalWidth,
          ease: "none",
          scrollTrigger: {
            trigger: ".par-gallery-section",
            start: "top top",
            end: `+=${totalWidth}px`,
            pin: true,
            scrub: 1,
          },
        });
      }

      // Venue cards stagger
      gsap.from(".par-venue-card", {
        opacity: 0,
        y: 40,
        stagger: 0.15,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: { trigger: ".par-venue-section", start: "top 75%", toggleActions: "play reverse play reverse" },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  const dateStr = dateTime
    ? new Date(dateTime).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <main className="bg-[#fdf8f5] text-[#3d2c26] overflow-x-hidden">
      {/* ── Hero: Multi-layer parallax ──────────────────────────────── */}
      <section className="par-hero relative h-screen flex items-center justify-center overflow-hidden">
        {/* Layer 1 — background (slow) */}
        <div
          className="par-layer-back absolute inset-0 scale-110"
          style={{ backgroundImage: `url(${DEMO_IMAGES.hero})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        {/* Layer 2 — warm color overlay */}
        <div className="par-layer-mid absolute inset-0 bg-gradient-to-b from-rose-900/30 via-amber-900/20 to-rose-900/50" />
        {/* Floating petals */}
        {PETALS.map((p, i) => (
          <div
            key={i}
            className="par-layer-front absolute pointer-events-none"
            style={{
              top: p.top, left: p.left,
              animation: `float ${3 + i * 0.5}s ease-in-out ${p.delay}s infinite alternate`,
              transform: `rotate(${p.rotate}deg)`,
            }}
          >
            <PetalSvg size={p.size} />
          </div>
        ))}
        {/* Title */}
        <div className="par-layer-front relative z-10 text-center px-6">
          <p className="text-xs tracking-[0.5em] uppercase text-white/60 mb-6">Davetiye</p>
          <h1 className="font-serif text-5xl sm:text-7xl font-light text-white drop-shadow-lg leading-tight">
            {title}
          </h1>
          {dateStr && (
            <p className="mt-6 text-sm tracking-widest text-white/70 uppercase">{dateStr}</p>
          )}
        </div>
      </section>

      {/* Float animation keyframes */}
      <style>{`@keyframes float { from { transform: translateY(0); } to { transform: translateY(-12px); } }`}</style>

      {/* ── Story circle ─────────────────────────────────────────────── */}
      <section className="par-story-section min-h-screen flex items-center justify-center py-24 px-6 bg-[#fdf8f5]">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-12">
          {/* Circle image mask — expands on scroll */}
          <div className="par-story-circle flex-shrink-0 w-64 h-64 sm:w-80 sm:h-80 rounded-full overflow-hidden shadow-2xl ring-4 ring-rose-200/50">
            <img src={DEMO_IMAGES.gallery[0]} alt="" className="w-full h-full object-cover" />
          </div>
          {/* Story text */}
          <div className="max-w-md">
            <div className="w-8 h-px bg-rose-300 mb-6" />
            <p className="text-xs tracking-[0.4em] uppercase text-rose-400 mb-4">Hikayemiz</p>
            <p className="text-xl font-serif font-light text-[#5c3d35] leading-relaxed">
              {description ?? "İki yıl önce bir yaz akşamı tanıştık. Sizi bu özel günümüze davet etmekten mutluluk duyuyoruz."}
            </p>
          </div>
        </div>
      </section>

      {/* ── Horizontal Gallery ───────────────────────────────────────── */}
      <section className="par-gallery-section overflow-hidden bg-white">
        <div className="par-gallery-track flex gap-4 p-6" style={{ width: "max-content" }}>
          {[...DEMO_IMAGES.gallery, ...DEMO_IMAGES.gallery].map((src, i) => (
            <div key={i} className="flex-shrink-0 w-72 h-96 rounded-2xl overflow-hidden shadow-lg">
              <img src={src} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </section>

      {/* ── Venue ────────────────────────────────────────────────────── */}
      <section className="par-venue-section min-h-[70vh] flex items-center py-24 px-6 bg-[#fdf0ec]">
        <div className="max-w-4xl mx-auto w-full grid sm:grid-cols-2 gap-6">
          <div className="par-venue-card bg-white rounded-2xl p-8 shadow-sm border border-rose-100">
            <p className="text-xs tracking-widest uppercase text-rose-300 mb-3">Tarih & Saat</p>
            <p className="text-2xl font-serif font-light text-[#3d2c26]">{dateStr ?? "Açıklanacak"}</p>
            {dateTime && (
              <p className="mt-2 text-rose-400">
                {new Date(dateTime).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
          </div>
          <div className="par-venue-card bg-white rounded-2xl p-8 shadow-sm border border-rose-100">
            <p className="text-xs tracking-widest uppercase text-rose-300 mb-3">Mekan</p>
            <p className="text-2xl font-serif font-light text-[#3d2c26] leading-snug">
              {location ?? "Mekan Açıklanacak"}
            </p>
          </div>
        </div>
      </section>

      {/* ── Form ─────────────────────────────────────────────────────── */}
      <section className="min-h-screen flex items-center justify-center bg-rose-50 px-6 py-24">
        <div className="w-full max-w-md">
          <p className="text-center text-xs tracking-[0.4em] uppercase text-rose-300 mb-4">Katılım</p>
          <h2 className="text-center text-3xl font-serif font-light text-[#3d2c26] mb-10">
            Bize Katılın
          </h2>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
